import { useContext, useEffect, useState, useRef } from 'react'


import { Mode } from './Mode'

import { About } from './About'
import globalStyle from '../../styles/common.module.css'
import style from './story.module.css'

import { CollectionContext } from '../../state/CollectionContext'
import { ReviewComments } from '../comments/ReviewComments'
import { UserComments } from '../comments/UserComments'
import { Nav } from './Nav'
import { EditArticle } from '../article/EditArticle'
import { ReviewArticle } from '../article/ReviewArticle'
import { Videos } from './Videos'
import { MoreIcon } from '../pages/MoreIcon'

type StoryDropdownProps = {
  stories: Array<{ id: string; title: string; slug?: string | null; isActive: boolean }>
  onSelectStory: (storyId: string) => void
}

function StoryDropdown({ stories, onSelectStory }: StoryDropdownProps) {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('pointerdown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('pointerdown', handleClickOutside)
    }
  }, [isOpen])

  const handleBlur = (e: React.FocusEvent) => {
    // Don't close if focus is moving to an element inside the dropdown
    if (dropdownRef.current && dropdownRef.current.contains(e.relatedTarget as Node)) {
      return
    }
    setIsOpen(false)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      setIsOpen(!isOpen)
    }
  }

  const storyCount = stories.length

  const handleStoryClick = (storyId: string, storySlug: string | null | undefined) => {
    onSelectStory(storyId)  
    setIsOpen(false)
  }

  return (
    <div className={style.storyDropdown} ref={dropdownRef}>
      <div
        className={style.storyDropdownTrigger}
        onClick={() => setIsOpen(!isOpen)}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        tabIndex={0}
        role="button"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <span className={style.storyDropdownText}>
          {storyCount} {storyCount === 1 ? 'excerpt' : 'excerpts'}
        </span>
        <MoreIcon isOpen={isOpen} />
      </div>
      {isOpen && (
        <div className={style.storyDropdownPopover}>
          <div className={style.storyDropdownList}>
            {stories.map((story, index) => (
              <div
                key={story.id}
                className={[
                  style.storyDropdownItem,
                  story.isActive ? style.storyDropdownItemActive : '',
                ].join(' ')}
                onClick={() => handleStoryClick(story.id, story.slug)}  
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    handleStoryClick(story.id, story.slug)
                  }
                }}
              >
                <span className={style.storyDropdownNumber}>{index + 1}</span>
                <span className={style.storyDropdownTitle}>{story.title}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

function Story({collectionName}: {collectionName: string}) {
  const context = useContext(CollectionContext)
  const storyIndex = context.state.findIndex((story) => story.isActive)
  const story = context.state[storyIndex];

  if (!story) return null

  return (
    <div className={[globalStyle.grid10, globalStyle.gridCenter].join(' ')}>
      <div className={[style.header].join(' ')}>
        <div className={[style.collectionHeader].join(' ')}>
          <h2 className={[globalStyle.heading2].join(' ')}>{collectionName}</h2>
        </div>
        <StoryDropdown 
          stories={context.state} 
          onSelectStory={(storyId) => context.dispatch({ type: 'select_story', id: storyId })}
        />
      </div>

      <div className={style.storyHeader}>
        <Mode title={story.title} />
        {story.mode !== 'edit' ? null : <About body={story.context} descriptor={story.descriptor} />}
      </div>

      <div className={[globalStyle.gridFlex].join(' ')}>
        <div className={[globalStyle.grid6, style.sticky].join(' ')}>
          <div className={style.article}>
            {story.mode === 'edit' ? (
              <EditArticle />
            ) : story.mode === 'review' ? (
              <ReviewArticle />
            ) : story.mode === 'final' ? (
              <div
                className={globalStyle.bodyLG}
                dangerouslySetInnerHTML={{ __html: story.cleanedText.html }}
              />
            ) : <Videos items={story.videos} />
            }
          </div>

          <Nav />
        </div>

        <div className={globalStyle.grid3}>
          {story.mode === 'review' ? (
            <ReviewComments />
          ) : story.mode === 'edit' ? (
            <UserComments />
          ) : null}
        </div>
      </div>
    </div>
  )
}

export { Story }
